/**
 * 邮箱配置表单组件
 * 用于新增和编辑邮箱配置
 * 优化后的简洁版本
 */

import React, { useState, useEffect } from "react";
import { Form, Input, Button, Space, message, Divider, Alert, Typography } from "antd";
import { ExperimentOutlined, SaveOutlined, MailOutlined } from "@ant-design/icons";
import { userEmailConfigService } from "../../../api/enhancedApi";

const { Text } = Typography;

const EmailConfigForm = ({ initialValues, onSuccess, onCancel }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const isEditing = !!initialValues;

    // 初始化表单值
    useEffect(() => {
        if (initialValues) {
            // 从完整邮箱地址中提取用户名
            const emailAddress = initialValues.email_address || "";
            const emailUsername = emailAddress.replace("@flymeta.com", "");

            form.setFieldsValue({
                config_name: initialValues.config_name,
                email_username: emailUsername,
                display_name: initialValues.display_name,
                smtp_username: initialValues.smtp_username,
                // 编辑时不显示密码
                smtp_password: undefined,
            });
        } else {
            // 新增时设置默认值
            form.setFieldsValue({
                config_name: "我的邮箱",
            });
        }
    }, [initialValues, form]);

    // 测试邮箱配置
    const handleTest = async () => {
        try {
            const values = await form.validateFields();
            setTesting(true);
            setTestResult(null);

            // 构建完整的邮箱地址
            const email_address = values.email_username + "@flymeta.com";

            if (isEditing) {
                // 编辑模式：检查是否有配置变更
                const hasChanges = values.config_name !== initialValues.config_name || email_address !== initialValues.email_address || values.display_name !== (initialValues.display_name || "") || values.smtp_username !== initialValues.smtp_username || (values.smtp_password && values.smtp_password.trim() !== "");

                if (hasChanges) {
                    // 有变更：使用当前输入的配置进行测试
                    // 注：服务器配置由后端固定，前端只传用户名和密码
                    const tempConfig = {
                        smtp_username: values.smtp_username,
                    };

                    // 只有输入了密码才传递密码参数
                    if (values.smtp_password && values.smtp_password.trim() !== "") {
                        tempConfig.smtp_password = values.smtp_password;
                    }

                    const response = await userEmailConfigService.testEmailConfig(initialValues.id, tempConfig);
                    if (response.data?.success) {
                        setTestResult({
                            success: true,
                            message: response.data.message + " (使用当前输入的配置测试)",
                            isUsingCurrentConfig: true,
                        });
                        message.success("邮箱测试成功（使用当前输入的配置）");
                    } else {
                        setTestResult({
                            success: false,
                            message: response.data?.message || "测试失败",
                            isUsingCurrentConfig: true,
                        });
                        message.error("邮箱测试失败");
                    }
                } else {
                    // 无变更：使用保存的配置测试
                    const response = await userEmailConfigService.testEmailConfig(initialValues.id);
                    if (response.data?.success) {
                        setTestResult({
                            success: true,
                            message: response.data.message + " (使用已保存的配置测试)",
                            isUsingCurrentConfig: false,
                        });
                        message.success("邮箱测试成功（使用已保存的配置）");
                    } else {
                        setTestResult({
                            success: false,
                            message: response.data?.message || "测试失败",
                            isUsingCurrentConfig: false,
                        });
                        message.error("邮箱测试失败");
                    }
                }
            } else {
                // 新增模式：先保存配置再测试
                // 注：服务器配置由后端固定，前端只传必要字段
                const configData = {
                    config_name: values.config_name,
                    email_address: email_address,
                    display_name: values.display_name,
                    smtp_username: values.smtp_username,
                    smtp_password: values.smtp_password,
                };

                const createResponse = await userEmailConfigService.createEmailConfig(configData);
                if (createResponse.data?.success) {
                    const configId = createResponse.data.data?.id;
                    if (configId) {
                        const testResponse = await userEmailConfigService.testEmailConfig(configId);
                        if (testResponse.data?.success) {
                            setTestResult({ success: true, message: testResponse.data.message });
                            message.success("邮箱配置保存并测试成功");
                            onSuccess && onSuccess();
                        } else {
                            setTestResult({ success: false, message: testResponse.data?.message || "测试失败" });
                            message.warning("邮箱配置已保存，但测试失败");
                        }
                    }
                } else {
                    setTestResult({ success: false, message: createResponse.data?.message || "保存失败" });
                    message.error("保存邮箱配置失败");
                }
            }
        } catch (error) {
            console.error("邮箱测试失败:", error);
            setTestResult({ success: false, message: "测试失败，请检查配置" });
            message.error("邮箱测试失败");
        } finally {
            setTesting(false);
        }
    };

    // 提交表单（仅编辑模式使用）
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // 构建完整的邮箱地址
            const email_address = values.email_username + "@flymeta.com";

            const updateData = {
                config_name: values.config_name,
                email_address: email_address,
                display_name: values.display_name,
                smtp_username: values.smtp_username,
            };

            // 只有输入了密码才更新密码
            if (values.smtp_password) {
                updateData.smtp_password = values.smtp_password;
            }

            const response = await userEmailConfigService.updateEmailConfig(initialValues.id, updateData);

            if (response.data?.success) {
                message.success("更新成功");
                onSuccess && onSuccess();
            } else {
                message.error(response.data?.message || "更新失败");
            }
        } catch (error) {
            console.error("提交失败:", error);
            message.error("更新失败");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: "8px 0" }}>
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                {/* 基本信息 */}
                <div style={{ marginBottom: 24 }}>
                    <Text strong style={{ fontSize: 16, marginBottom: 16, display: "block" }}>
                        <MailOutlined style={{ marginRight: 8 }} />
                        基本信息
                    </Text>

                    <Form.Item
                        name="config_name"
                        label="配置名称"
                        rules={[
                            { required: true, message: "请输入配置名称" },
                            { max: 100, message: "配置名称不能超过100个字符" },
                        ]}
                        style={{ marginBottom: 16 }}
                    >
                        <Input placeholder="如：我的工作邮箱" />
                    </Form.Item>

                    <Form.Item
                        name="email_username"
                        label="邮箱地址"
                        rules={[
                            { required: true, message: "请输入邮箱用户名" },
                            {
                                pattern: /^[a-zA-Z0-9._%+-]+$/,
                                message: "用户名只能包含字母、数字、点号、下划线等",
                            },
                        ]}
                        style={{ marginBottom: 16 }}
                    >
                        <Input placeholder="请输入用户名" addonAfter="@flymeta.com" />
                    </Form.Item>

                    <Form.Item name="display_name" label="发件人显示名称" rules={[{ max: 100, message: "显示名称不能超过100个字符" }]} style={{ marginBottom: 0 }}>
                        <Input placeholder="如：张三" />
                    </Form.Item>
                </div>

                <Divider />

                {/* SMTP配置 */}
                <div style={{ marginBottom: 24 }}>
                    <Text strong style={{ fontSize: 16, marginBottom: 16, display: "block" }}>
                        SMTP配置
                    </Text>

                    <Alert message="服务器配置" description="smtphz.qiye.163.com:465 (SSL)" type="info" showIcon style={{ marginBottom: 16 }} />

                    <Form.Item
                        name="smtp_username"
                        label="SMTP用户名"
                        rules={[
                            { required: true, message: "请输入SMTP用户名" },
                            { max: 255, message: "SMTP用户名不能超过255个字符" },
                        ]}
                        style={{ marginBottom: 16 }}
                    >
                        <Input placeholder="通常与邮箱地址相同" />
                    </Form.Item>

                    <Form.Item name="smtp_password" label={isEditing ? "SMTP密码（留空表示不修改）" : "SMTP密码"} rules={isEditing ? [] : [{ required: true, message: "请输入SMTP密码" }]} style={{ marginBottom: 0 }}>
                        <Input.Password placeholder={isEditing ? "留空表示不修改密码" : "请输入SMTP密码或授权码"} />
                    </Form.Item>
                </div>

                {/* 测试结果 */}
                {testResult && (
                    <Alert
                        message={testResult.success ? "测试成功" : "测试失败"}
                        description={
                            <div>
                                <div>{testResult.message}</div>
                                {isEditing && testResult.isUsingCurrentConfig !== undefined && <div style={{ marginTop: 8, fontSize: "12px", opacity: 0.8 }}>{testResult.isUsingCurrentConfig ? `💡 提示：测试使用的是当前输入的配置，如需保存请点击"更新配置"` : "💡 提示：测试使用的是已保存的配置"}</div>}
                            </div>
                        }
                        type={testResult.success ? "success" : "error"}
                        showIcon
                        style={{ marginBottom: 24 }}
                    />
                )}

                <Divider />

                {/* 操作按钮 */}
                <div style={{ textAlign: "right" }}>
                    <Space>
                        <Button onClick={onCancel}>取消</Button>

                        <Button icon={<ExperimentOutlined />} loading={testing} onClick={handleTest}>
                            {isEditing ? "测试连接" : "保存并测试"}
                        </Button>

                        {isEditing && (
                            <Button type="primary" icon={<SaveOutlined />} loading={loading} htmlType="submit">
                                更新配置
                            </Button>
                        )}
                    </Space>
                </div>
            </Form>
        </div>
    );
};

export default EmailConfigForm;
